import { keyBy, uniq } from "lodash-es";
import { call, put } from "redux-saga/effects";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import {
  Company,
  EntityType,
  EntityTypesSupportedByMapsPage,
  Person,
} from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { PinLegendsFilter } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import { clearTimelineEventsPinColor } from "@app/scene/dashboard/store/actions";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import TimelineEvent from "@app/types/events/TimelineEvent";
import getPinLegendsFilter from "@app/util/map/getPinLegendsFilter";
import getEventPinColor from "@app/util/timeline/getEventPinColor";
import getTimelineEventEntityRef from "@app/util/timeline/getTimelineEventEntityRef";

// Fills in each event's legend color in the same pass that loaded the page, so a card always has its
// color before it can be expanded. Mutates: the events are the freshly parsed response and have not
// reached the store yet, so nothing else holds a reference to them.
// A failure must not cost the user their timeline — the events are left as they are and every card
// falls back to its entity-type color.
function* fillTimelineEventsPinColor(orgId: Organization["id"], events: TimelineEvent[]) {
  const refs = events.map(getTimelineEventEntityRef).filter(isDefined);
  const accountIds = uniq(
    refs.filter((ref) => ref.entityType === EntityType.COMPANY).map((ref) => ref.entityId)
  );
  const contactIds = uniq(
    refs.filter((ref) => ref.entityType === EntityType.PERSON).map((ref) => ref.entityId)
  );
  if (accountIds.length === 0 && contactIds.length === 0) {
    return;
  }

  try {
    // Only the entity types the page actually holds: asking for one with no ids makes the backend
    // resolve and pre-classify a branch nothing reads. includeGroups is read per entity by the
    // backend and includeCustomFields off the root, so a group- or custom-field-based legend
    // resolves to a default if either sits in the other place.
    const entityTypes: EntityTypesSupportedByMapsPage[] = [];
    const entities: Record<string, unknown> = {};
    if (accountIds.length > 0) {
      entityTypes.push(EntityType.COMPANY);
      entities[EntityType.COMPANY] = { $and: [{ id: { $in: accountIds } }], includeGroups: true };
    }
    if (contactIds.length > 0) {
      entityTypes.push(EntityType.PERSON);
      entities[EntityType.PERSON] = { $and: [{ id: { $in: contactIds } }], includeGroups: true };
    }
    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, entityTypes);

    const response: ListResponse<Company | Person> = yield callApi("fetchPins", orgId, {
      $filters: { entities, includeCustomFields: true, pinLegends },
      $limit: accountIds.length + contactIds.length,
    });

    const accountPins = keyBy(
      response.data.filter((entity): entity is Company => entity.entity === EntityType.COMPANY),
      (entity) => entity.id
    ) as Record<Company["id"], Company & { shape?: MarkerShape }>;
    const contactPins = keyBy(
      response.data.filter((entity): entity is Person => entity.entity === EntityType.PERSON),
      (entity) => entity.id
    ) as Record<Person["id"], Person & { shape?: MarkerShape }>;

    events.forEach((event) => {
      const pinColor = getEventPinColor(event, accountPins, contactPins);
      if (pinColor) {
        event.pinColor = pinColor;
      }
    });
  } catch (error) {
    // No notification: the cards fall back to their entity-type color, so the user loses nothing.
    // Already-resolved colors are dropped too, so the list falls back together instead of leaving
    // this page a different color from the ones around it.
    yield put(clearTimelineEventsPinColor());
    yield put(handleError({ displayNotification: false, error }));
  }
}

export default fillTimelineEventsPinColor;
